import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ChevronDown, ChevronRight, Loader2, Wand2, ArrowRightLeft, X, Eye, Trash2, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = '/api';

const TIER_STYLE = {
  tier_1a_archive:     { border: 'border-rose-200',   bg: 'bg-rose-50/40',   accent: 'text-rose-700',   button: 'bg-rose-600 hover:bg-rose-700',   action: 'Delete all', icon: Trash2 },
  tier_1b_to_investor: { border: 'border-emerald-200',bg: 'bg-emerald-50/40',accent: 'text-emerald-700',button: 'bg-emerald-600 hover:bg-emerald-700', action: 'Move to Investors', icon: ArrowRightLeft },
  tier_2_to_biotech:   { border: 'border-sky-200',    bg: 'bg-sky-50/40',    accent: 'text-sky-700',    button: '',                                action: '', icon: ArrowRightLeft },
  tier_3_insufficient: { border: 'border-amber-200',  bg: 'bg-amber-50/40',  accent: 'text-amber-700',  button: '',                                  action: '', icon: Eye },
  tier_4_dismiss:      { border: 'border-slate-200',  bg: 'bg-slate-50/40',  accent: 'text-slate-600',  button: 'bg-slate-700 hover:bg-slate-800', action: 'Dismiss all', icon: X },
  tier_5_review:       { border: 'border-violet-200', bg: 'bg-violet-50/40', accent: 'text-violet-700', button: '',                                  action: '', icon: Eye },
};

export default function AuditTriagePanel({ onApplied }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyTier, setBusyTier] = useState(null);
  const [busyRow, setBusyRow] = useState(null);
  const [expanded, setExpanded] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/audit/triage`, { withCredentials: true });
      setData(r.data);
    } catch (e) {
      toast.error('Failed to load triage');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const apply = async (tierKey, label, count) => {
    const isDelete = tierKey === 'tier_1a_archive';
    const confirmMsg = isDelete
      ? `⚠️ Permanently delete ${count} record${count !== 1 ? 's' : ''} from the active CRM?\n\nThey will be removed from biotech_companies / company_profiles / investors and can NEVER receive emails or appear in any list. A snapshot is saved to deleted_records collection for emergency recovery.\n\nProceed?`
      : `Apply ${label} to ${count} record${count !== 1 ? 's' : ''}? This is reversible from each record's profile page.`;
    if (!window.confirm(confirmMsg)) return;
    setBusyTier(tierKey);
    try {
      const r = await axios.post(`${API}/audit/triage/apply`, { tier: tierKey }, { withCredentials: true });
      const summary = [];
      if (r.data.archived) summary.push(`${r.data.archived} ${isDelete ? 'deleted' : 'archived'}`);
      if (r.data.moved) summary.push(`${r.data.moved} moved`);
      if (r.data.dismissed) summary.push(`${r.data.dismissed} dismissed`);
      if (r.data.skipped_bd_verified) summary.push(`${r.data.skipped_bd_verified} skipped (verified)`);
      toast.success(`${label} done · ${summary.join(' · ') || 'no changes'}`);
      await load();
      if (onApplied) onApplied();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Apply failed');
    }
    setBusyTier(null);
  };

  const applyRecord = async (findingId, action, recordName) => {
    const labels = { move_to_biotech: 'Move to Biotech', move_to_investor: 'Move to Investors', keep: 'Keep in Pharma', delete: 'Delete' };
    if (action === 'delete' && !window.confirm(`Permanently delete ${recordName}? Snapshot saved for recovery.`)) return;
    setBusyRow(findingId);
    try {
      await axios.post(`${API}/audit/triage/apply-record`, { finding_id: findingId, action }, { withCredentials: true });
      toast.success(`${recordName} · ${labels[action]}`);
      await load();
      if (onApplied) onApplied();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed');
    }
    setBusyRow(null);
  };

  if (loading && !data) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg px-5 py-8 flex items-center gap-3 text-slate-500 text-sm">
        <Loader2 className="w-4 h-4 animate-spin" /> Classifying findings into tiers…
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="space-y-3" data-testid="triage-panel">
      <div className="flex items-center gap-2 text-sm">
        <Wand2 className="w-4 h-4 text-violet-600" />
        <span className="font-semibold text-slate-900">Smart Triage</span>
        <span className="text-slate-400">— {data.grand_total} open findings auto-classified into 6 tiers. Bulk-action the obvious ones, eyeball the rest.</span>
      </div>

      <div className="space-y-2">
        {data.tiers.map(t => {
          const style = TIER_STYLE[t.key] || TIER_STYLE.tier_5_review;
          const Icon = style.icon;
          const isOpen = expanded[t.key];
          const isBulk = !!style.button && t.items.length > 0;
          return (
            <div key={t.key} className={`border rounded-lg ${style.border} ${style.bg}`} data-testid={`tier-${t.key}`}>
              <button
                onClick={() => setExpanded(prev => ({ ...prev, [t.key]: !prev[t.key] }))}
                className="w-full flex items-center justify-between gap-4 px-4 py-3 text-left hover:bg-white/40 transition-colors"
              >
                <div className="flex items-center gap-3 min-w-0">
                  {isOpen ? <ChevronDown className="w-4 h-4 text-slate-500 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-500 shrink-0" />}
                  <Icon className={`w-4 h-4 ${style.accent} shrink-0`} />
                  <div className="min-w-0">
                    <div className={`text-sm font-semibold ${style.accent}`}>{t.label} <span className="text-slate-400 font-normal ml-1">({t.items.length})</span></div>
                    <div className="text-[11px] text-slate-500 mt-0.5 truncate">{t.blurb}</div>
                  </div>
                </div>
                {isBulk && (
                  <Button
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); apply(t.key, style.action, t.items.length); }}
                    disabled={busyTier === t.key || t.items.length === 0}
                    className={`${style.button} text-[11px] h-7 shrink-0`}
                    data-testid={`apply-tier-${t.key}`}
                  >
                    {busyTier === t.key ? <Loader2 className="w-3 h-3 animate-spin" /> : style.action}
                  </Button>
                )}
              </button>

              {isOpen && (
                <div className="border-t border-slate-200/60 bg-white/60 px-4 py-2 max-h-96 overflow-y-auto">
                  {t.items.length === 0 ? (
                    <div className="text-xs text-slate-400 py-3">No records in this tier.</div>
                  ) : (
                    <ul className="divide-y divide-slate-100">
                      {t.items.map(item => {
                        const showRowActions = ['tier_2_to_biotech', 'tier_3_insufficient', 'tier_5_review'].includes(t.key);
                        const rowBusy = busyRow === item.finding_id;
                        return (
                          <li key={item.finding_id} className="py-2 text-xs flex items-start gap-3" data-testid={`tier-${t.key}-item-${item.finding_id}`}>
                            <div className="font-medium text-slate-900 w-56 shrink-0 truncate flex items-center gap-1.5">
                              {item.record_name}
                              {item.ai_mapping_count > 0 && (
                                <span className="inline-flex items-center gap-0.5 text-[9px] font-semibold text-emerald-700 bg-emerald-100 border border-emerald-200 rounded-full px-1.5 py-0.5" title={`${item.ai_mapping_count} AI mapping(s) flag this as in-licensing target`}>
                                  🎯 {item.ai_mapping_count}
                                </span>
                              )}
                            </div>
                            <div className="text-slate-400 w-20 shrink-0">{item.collection.replace('_companies', '').replace('_profiles', '').replace('biotech', 'Biotech').replace('company', 'Pharma').replace('investors', 'Investor')}</div>
                            <div className="text-slate-600 leading-relaxed flex-1">{item.reason_text}</div>
                            {showRowActions && (
                              <div className="flex items-center gap-1 shrink-0">
                                <button
                                  onClick={() => applyRecord(item.finding_id, 'keep', item.record_name)}
                                  disabled={rowBusy}
                                  className="text-[10px] px-2 py-1 rounded border border-slate-200 text-slate-600 hover:bg-slate-100 disabled:opacity-50 flex items-center gap-1"
                                  data-testid={`row-keep-${item.finding_id}`}
                                  title="Keep in this collection — record stays where it is, gets bd_verified flag so it won't be re-flagged in future audits"
                                >
                                  <Check className="w-3 h-3" /> Keep
                                </button>
                                {item.collection === 'company_profiles' && t.key === 'tier_2_to_biotech' && (
                                  <button
                                    onClick={() => applyRecord(item.finding_id, 'move_to_biotech', item.record_name)}
                                    disabled={rowBusy}
                                    className="text-[10px] px-2 py-1 rounded bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-50 flex items-center gap-1"
                                    data-testid={`row-move-biotech-${item.finding_id}`}
                                  >
                                    {rowBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <><ArrowRightLeft className="w-3 h-3" /> Move to Biotech</>}
                                  </button>
                                )}
                                <button
                                  onClick={() => applyRecord(item.finding_id, 'delete', item.record_name)}
                                  disabled={rowBusy}
                                  className="text-[10px] px-2 py-1 rounded border border-rose-200 text-rose-700 hover:bg-rose-50 disabled:opacity-50 flex items-center gap-1"
                                  data-testid={`row-delete-${item.finding_id}`}
                                  title="Permanently delete (snapshot saved)"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
